/**
 * Transform session_games API rows for Session Performance GameHistoryTable.
 */

/**
 * @param {Array} eloRows
 * @returns {Map<string, { elo_change?: number, new_value?: number, pair_type?: string }>}
 */
export function buildEloByGamePlayerMap(eloRows) {
  const map = new Map();
  (eloRows || []).forEach((row) => {
    if (row.session_game_id && row.player_id) {
      map.set(`${row.session_game_id}:${row.player_id}`, row);
    }
  });
  return map;
}

/**
 * Average pre-game (`old_value`) rating for a set of players in one game.
 * Matches sessionPlayerInsights / headToHeadService team-rating convention.
 */
export function averageTeamOldElo(playerIds, eloMap, gameId) {
  const values = (playerIds || [])
    .map((pid) => getEloFromMap(eloMap, gameId, pid)?.old_value)
    .filter((v) => v != null);
  if (!values.length) return null;
  return Math.round(values.reduce((sum, v) => sum + v, 0) / values.length);
}

/**
 * @param {Map} eloMap
 * @param {string} gameId
 * @param {string} playerId
 */
export function getEloFromMap(eloMap, gameId, playerId) {
  if (!eloMap || !gameId || !playerId) return null;
  return eloMap.get(`${gameId}:${playerId}`) || null;
}

/**
 * @param {object} sessionMeta
 * @param {string} sessionMeta.sessionId
 * @param {string} [sessionMeta.clubName]
 * @param {string} [sessionMeta.sessionName]
 */
function baseSessionGameFields(game, sessionMeta) {
  return {
    clubName: sessionMeta.clubName || '',
    sessionName: sessionMeta.sessionName || '',
    created_at: game.created_at,
    session_games: {
      session_id: sessionMeta.sessionId,
      team_1_score: game.team_1_score,
      team_2_score: game.team_2_score,
      created_at: game.created_at,
    },
  };
}

/**
 * One GameHistoryTable row per game the player joined (player-centric layout).
 * @param {Array} sessionGames
 * @param {string} playerId
 * @param {{ sessionId: string, clubName?: string, sessionName?: string }} sessionMeta
 * @param {Map} eloMap
 * @returns {Array}
 */
export function buildPlayerGameHistoryRows(sessionGames, playerId, sessionMeta, eloMap) {
  if (!sessionGames?.length || !playerId) return [];

  return sessionGames
    .map((game) => {
      const playerGame = game.players_session_games?.find((pg) => pg.player_id === playerId);
      if (!playerGame) return null;

      const gamePlayers = game.players_session_games || [];
      const eloData = getEloFromMap(eloMap, game.id, playerId);

      const teammates = gamePlayers
        .filter((p) => p.team_number === playerGame.team_number && p.player_id !== playerId)
        .map((p) => p.players)
        .filter(Boolean);
      const opponents = gamePlayers
        .filter((p) => p.team_number !== playerGame.team_number)
        .map((p) => p.players)
        .filter(Boolean);

      const myTeamIds = [playerId, ...teammates.map((t) => t.id)].filter(Boolean);
      const oppTeamIds = opponents.map((o) => o.id).filter(Boolean);

      return {
        id: game.id,
        player_id: playerId,
        team_number: playerGame.team_number,
        result: playerGame.result,
        teammates,
        opponents,
        eloChange: eloData?.elo_change ?? null,
        playerElo: eloData?.new_value ?? null,
        myTeamAvgElo: averageTeamOldElo(myTeamIds, eloMap, game.id),
        oppTeamAvgElo: averageTeamOldElo(oppTeamIds, eloMap, game.id),
        ...baseSessionGameFields(game, sessionMeta),
      };
    })
    .filter(Boolean);
}

/**
 * One GameHistoryTable row per session game (team vs team layout).
 * @param {Array} sessionGames
 * @param {{ sessionId: string, clubName?: string, sessionName?: string }} sessionMeta
 * @param {Map} eloMap
 * @param {string} [highlightPlayerId] - Page subject; highlights their team when they played
 * @returns {Array}
 */
export function buildSessionTeamGameHistoryRows(
  sessionGames,
  sessionMeta,
  eloMap,
  highlightPlayerId = null,
) {
  if (!sessionGames?.length) return [];

  return sessionGames.map((game) => {
    const gamePlayers = game.players_session_games || [];
    const team1Players = gamePlayers
      .filter((p) => p.team_number === 1)
      .map((p) => p.players)
      .filter(Boolean);
    const team2Players = gamePlayers
      .filter((p) => p.team_number === 2)
      .map((p) => p.players)
      .filter(Boolean);

    const eloByPlayerId = {};
    for (const pg of gamePlayers) {
      const pid = pg.player_id;
      if (!pid) continue;
      const elo = getEloFromMap(eloMap, game.id, pid);
      if (elo?.elo_change != null) {
        eloByPlayerId[pid] = elo.elo_change;
      }
    }

    let highlightTeamNumber = null;
    if (highlightPlayerId) {
      const row = gamePlayers.find((p) => p.player_id === highlightPlayerId);
      if (row) highlightTeamNumber = row.team_number;
    }

    const team1Ids = gamePlayers.filter((p) => p.team_number === 1).map((p) => p.player_id);
    const team2Ids = gamePlayers.filter((p) => p.team_number === 2).map((p) => p.player_id);

    return {
      id: game.id,
      rowLayout: 'sessionTeams',
      team1Players,
      team2Players,
      highlightTeamNumber,
      highlightPlayerId,
      eloByPlayerId,
      team1AvgElo: averageTeamOldElo(team1Ids, eloMap, game.id),
      team2AvgElo: averageTeamOldElo(team2Ids, eloMap, game.id),
      ...baseSessionGameFields(game, sessionMeta),
    };
  });
}
